import { useCallback, useEffect, useState } from 'react';

import { ancanaApi } from '../../../api/ancanaApi';
import { IMembership, IOperator } from '../../../types/models/tenancy';

const PATH = 'tenancy/operators/';

/** Read an axios error into the one line worth showing an operator. */
export const errorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  if (typeof data === 'string') return data;
  if (data?.detail) return data.detail;
  if (data && typeof data === 'object') {
    // DRF field errors: `{"slug": ["operator with this slug already exists."]}`.
    const [field, messages] = Object.entries(data)[0] || [];
    if (field) {
      const text = Array.isArray(messages) ? messages.join(' ') : String(messages);
      return field === 'non_field_errors' ? text : `${field}: ${text}`;
    }
  }
  return error?.message || fallback;
};

export interface IFirstAdmin { email: string; first_name: string; last_name: string }
export interface IOnboardResult { operator: IOperator; membership: IMembership; invited: boolean }

export interface UseOperatorsResult {
  operators: IOperator[];
  isLoading: boolean;
  error?: string;
  reload: () => Promise<void>;
  create: (data: Partial<IOperator>) => Promise<IOperator>;
  onboard: (data: Partial<IOperator>, admin: IFirstAdmin) => Promise<IOnboardResult>;
  patch: (id: number, data: Partial<IOperator>) => Promise<IOperator>;
}

/**
 * The tenant roster.
 *
 * Fetched whole rather than paginated: there are a handful of operators, the
 * panel shows all of them side by side, and `member_count` plus the nested
 * branding already come down with each row, so a page-at-a-time list would add
 * a control for no benefit. `page_size` is pinned high because the API's
 * default `PAGE_SIZE` is 3.
 */
const useOperators = (): UseOperatorsResult => {
  const [operators, setOperators] = useState<IOperator[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  const reload = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await ancanaApi.get(`${PATH}?page_size=100`);
      setOperators(response.data?.results || response.data || []);
      setError(undefined);
    } catch (e: any) {
      setError(errorMessage(e, 'Could not load tenants.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const create = useCallback(async (data: Partial<IOperator>) => {
    const response = await ancanaApi.post(PATH, data);
    await reload();
    return response.data as IOperator;
  }, [reload]);

  /**
   * A tenant plus its first admin, all or nothing (`operators/onboard/`):
   * defaults seeded, admin invited by email to the Caim console.
   */
  const onboard = useCallback(async (data: Partial<IOperator>, admin: IFirstAdmin) => {
    const response = await ancanaApi.post(`${PATH}onboard/`, { ...data, admin });
    await reload();
    return response.data as IOnboardResult;
  }, [reload]);

  const patch = useCallback(async (id: number, data: Partial<IOperator>) => {
    const response = await ancanaApi.patch(`${PATH}${id}/`, data);
    const updated = response.data as IOperator;
    // Patch in place instead of reloading: the detail pane is a controlled form
    // over the selected row, and swapping the whole list under it on every save
    // would reset scroll and flash the panel.
    setOperators(previous => previous.map(o => (o.id === id ? updated : o)));
    return updated;
  }, []);

  return { operators, isLoading, error, reload, create, onboard, patch };
};

export default useOperators;
